import EngineCheckout from "../components/checkout/EngineCheckout";
import React, { useCallback, useMemo, useEffect, useState } from "react";
import { useLocalSearchParams, router } from "expo-router";
import LButton from "@components/LButton";
import LText from "@components/LText";
import { theme } from "@theme";
import {
  initialWindowMetrics,
  useSafeAreaInsets,
} from "react-native-safe-area-context";
import Handle from "../components/common/Handle";
import BackgroundCircles from "../components/common/Background";
import LBox from "@components/LBox";
import Buttons from "../components";
import Ticket from "../components/tickets/Ticket";
import { type Access, useAccess } from "@hooks/trpc/useAccesses";
import { useAccessViewport } from "../config";
import { Platform, ScrollView } from "react-native";
import { useNavigationStore } from "@utils/navigation/useNavigationStore";

/** Select tickets for an event that is not on the engine (old rail). */
function Tickets() {
  const { width: screenWidth, height: screenHeight } = useAccessViewport();
  const { accessId: accessIdParam, unlockCode: unlockCodeParam } =
    useLocalSearchParams();
  const insets = useSafeAreaInsets();
  const accessId = useMemo(() => {
    return Array.isArray(accessIdParam)
      ? accessIdParam[0]
      : accessIdParam || "1";
  }, [accessIdParam]);
  const unlockCode =
    typeof unlockCodeParam === "string" ? unlockCodeParam : undefined;

  const dismiss = useCallback(() => {
    router.back();
  }, []);

  const { data, isLoading } = useAccess(accessId);
  const loaded: Access | undefined = data;
  // Music unlocks, and merch is bought, in a sheet over its Details
  // (LIS-1038): a link here goes there.
  const inSheet = loaded?.type === "music" || loaded?.type === "merch";
  const access = loaded?.type === "merch" ? undefined : loaded;
  useEffect(() => {
    if (!inSheet) return;
    router.replace({
      pathname: "/(app)/access/[accessId]",
      params: { accessId },
    });
  }, [inSheet, accessId]);

  // State managed at parent level
  const [numberOfTickets, setNumberOfTickets] = useState(1);
  const [selectedPrice, setSelectedPrice] = useState<number>(
    access?.price ?? 0,
  );

  const handleContinue = () => {
    router.push({
      pathname: "/(app)/access/[accessId]/checkout",
      params: {
        accessId,
        pricePerTicket: unlockCode ? 0 : selectedPrice,
        numberOfTickets,
        ...(unlockCode && { unlockCode }),
      },
    });
  };

  const paymentResult = useNavigationStore((state) => state.paymentResult);

  useEffect(() => {
    if (paymentResult === "success") {
      dismiss();
    }
  }, [paymentResult, dismiss]);

  if (isLoading || !access || access.type === "music") {
    return null;
  }

  const footerBottomMargin = Math.max(
    theme.spacing.m1 * 3,
    (Platform.OS === "android"
      ? initialWindowMetrics?.insets?.bottom ?? 0
      : insets.bottom) + theme.spacing.m1,
  );

  if (access?.engineEventId)
    return <EngineCheckout accessId={accessId} onClose={dismiss} />;

  return (
    <LBox minHeight={screenHeight}>
      <BackgroundCircles imageUrl={null} />
      <ScrollView
        contentContainerStyle={{
          backgroundColor: "transparent",
          padding: 20,
          paddingTop: theme.spacing.s4,
          paddingBottom:
            Platform.OS === "android"
              ? initialWindowMetrics?.insets?.bottom ?? 20
              : insets.bottom + theme.spacing.s3,
          gap: theme.spacing.s3,
          flex: 1,
          width: screenWidth,
        }}
        showsVerticalScrollIndicator={false}
      >
        {Platform.OS !== "web" && <Handle />}
        <LBox flex={1} justifyContent="space-between">
          <LBox gap="m1">
            <LBox>
              <LBox
                position="relative"
                flexDirection="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <LBox width={32} />
                <LText variant="Heading/H2/SemiBold" textAlign="center">
                  Select Tickets
                </LText>
                <Buttons.common.Close
                  onPress={() => dismiss()}
                  sentryEventName="AccessTickets.Close"
                />
              </LBox>
            </LBox>

            <Ticket
              title="Ticket"
              price={access.price}
              priceMax={access.priceMax ?? null}
              bookingFee={access.bookingFee ?? null}
              numberOfTickets={numberOfTickets}
              setNumberOfTickets={setNumberOfTickets}
              setSelectedPrice={setSelectedPrice}
            />
          </LBox>

          <LBox style={{ marginBottom: footerBottomMargin }}>
            <LButton
              label="Continue to Checkout"
              variant="roundedWhite"
              labelNode={
                <LText
                  variant="Body/B2/SemiBold"
                  textTransform="uppercase"
                  color="black"
                >
                  Continue to Checkout
                </LText>
              }
              onPress={handleContinue}
              sentryEventName="AccessTickets.Continue"
            />
          </LBox>
        </LBox>
      </ScrollView>
    </LBox>
  );
}
export default Tickets;
